import { asyncCallRoutePost } from '../helpers/api';

/**
 * Class for handling all custom functionality on view order page (/my-account/view-order/123/)
 */
export class OrderTitleForm {
	constructor( element ) {
		this.element = element;

		if ( ! this.element ) {
			return;
		}

		this.orderId = this.element.dataset.orderId;
		this.headingElement = this.element.querySelector( '.js-qala-b2b-order-title-heading' );
		this.inputElement = this.element.querySelector( '.js-qala-b2b-order-title-input' );
		this.editElement = this.element.querySelector( '.js-qala-b2b-order-title-edit' );
		this.saveElement = this.element.querySelector( '.js-qala-b2b-order-title-save' );
		this.cancelElement = this.element.querySelector( '.js-qala-b2b-order-title-cancel' );
		this.noticeElement = this.element.querySelector( '.js-qala-b2b-order-title-notice' );

		this.isEditing = false;

		this.routeUrl = window.QalaB2bWholesale.updateOrderRouteObj.routeUrl;
		this._wpnonce = window.QalaB2bWholesale.updateOrderRouteObj._wpnonce;
	}

	/**
	 * Initialize the view order page with all needed callbacks
	 *
	 * @return {void}
	 */
	init() {
		if ( ! this.element ) {
			return;
		}

		this.editElement.addEventListener( 'click', () => this.startEditing() );
		this.cancelElement.addEventListener( 'click', () => this.stopEditing() );
		this.saveElement.addEventListener( 'click', () => this.saveChange() );
	}

	/**
	 * Validates customer email, sets the first name last name if needed.
	 *
	 * @return {void}
	 */
	startEditing() {
		this.isEditing = true;
		this.inputElement.classList.remove( 'hidden' );
		this.saveElement.classList.remove( 'hidden' );
		this.cancelElement.classList.remove( 'hidden' );
		this.editElement.classList.add( 'hidden' );
		this.headingElement.classList.add( 'hidden' );

		this.inputElement.focus();
	}

	/**
	 * Stops editing
	 *
	 * @return {void}
	 */
	stopEditing() {
		this.isEditing = false;
		this.inputElement.classList.add( 'hidden' );
		this.saveElement.classList.add( 'hidden' );
		this.cancelElement.classList.add( 'hidden' );
		this.editElement.classList.remove( 'hidden' );
		this.headingElement.classList.remove( 'hidden' );
	}

	/**
	 * Saves the change
	 *
	 * @return {void}
	 */
	async saveChange() {
		await this.saveOrder();
		this.stopEditing();
	}

	/**
	 * Send the request for saving order.
	 *
	 * @return {void}
	 */
	async saveOrder() {
		this.startValidating();
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, {
				order_id: this.orderId,
				custom_order_title: this.inputElement.value,
			} );
			this.processResponse( response );
		} catch ( e ) {
			this.processErrorResponse();
		}

		this.endValidating();
	}

	/**
	 * Start the request for validating customer email.
	 *
	 * @return {void}
	 */
	startValidating() {
		this.cancelElement.disabled = true;
		this.saveElement.disabled = true;
		this.editElement.disabled = true;
		this.clearNotice();
	}

	/**
	 * End the request for validating customer email.
	 *
	 * @return {void}
	 */
	endValidating() {
		this.cancelElement.disabled = false;
		this.saveElement.disabled = false;
		this.editElement.disabled = false;
	}

	/**
	 * Process the response from the server, assuming everything is ok (no server error)
	 *
	 * @param {Object} response
	 */
	processResponse( response ) {
		this.noticeElement.innerHTML = response.message;
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.headingElement.innerHTML = this.inputElement.value;
			this.clearNotice();
		} else {
			this.setErrorNotice( response.message );
		}
	}

	/**
	 * Set the success notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setSuccessNotice( text ) {
		this.noticeElement.hidden = false;
		this.noticeElement.innerHTML = text;
		this.noticeElement.classList.remove( 'woocommerce-error' );
		this.noticeElement.classList.add( 'woocommerce-message' );
		this.hideNoticeAfterDelay( this.noticeElement );
	}

	/**
	 * Set the error notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setErrorNotice( text ) {
		this.noticeElement.hidden = false;
		this.noticeElement.innerHTML = text;
		this.noticeElement.classList.remove( 'woocommerce-message' );
		this.noticeElement.classList.add( 'woocommerce-error' );
		this.hideNoticeAfterDelay( this.noticeElement );
	}

	/**
	 * Clear the notice.
	 *
	 * @return {void}
	 */
	clearNotice() {
		this.noticeElement.innerHTML = '';
		this.noticeElement.classList.remove( 'woocommerce-message' );
		this.noticeElement.classList.remove( 'woocommerce-error' );
	}

	/**
	 * Process the error response from the server, happens if there is a server error (500)
	 *
	 * @return {void}
	 */
	processErrorResponse() {
		this.setErrorNotice( window.QalaB2bWholesale.text.unknownError );
	}

	/**
	 * Hide the notice after a delay.
	 *
	 * @param {Element} element
	 * @return {void}
	 */
	hideNoticeAfterDelay( element ) {
		setTimeout( () => {
			element.hidden = true;
		}, 5000 );
	}
}
